import {useEffect, useRef, useState} from "react";
import * as Sentry from "@sentry/react";
import {Button, Spin} from "antd";
import {FaUser} from "react-icons/fa";
import {IoPlay} from "react-icons/io5";
import {useLocation, useNavigate} from "react-router-dom";
import CommentSection from "./components/CommentSection";

import backIcon from "../../assets/images/arrow-left.svg";
import detailThumb from "../../assets/images/detail-thumb.webp";
import TatvaPracticecolorLogo from "../../assets/images/TatvaPracticecolor.png";
import LikeShareBookmark from "./components/LikeShareBookmark";
import OptimizedImage from "../../commonComponents/OptimizedImage";
import PodcastControls from "./components/PodcastControls";
import {createNativeMediaPlayer, supportsNativeHls} from "./nativeMediaPlayer";

import {getContentDetail, getContentUrl, getMediaData, getQuizDetail, getQuizStats} from "./service";
import useStore from "../../store/useStore";
import {MKPlatformType, MKPlayer, MKPlayerEvent} from "@mediakind/mkplayer"; // Import MKPlayer
import "@mediakind/mkplayer/mkplayer-ui.css";
import {MEDIAKIND_STREAMING_ORIGIN, MKPLAYER_KEY} from "../../envConstants";
import axios from "axios";
import {saveAs} from "file-saver";
import moment from "moment";
import {sendMessageToParent} from "../../utils/helper";
import {isZymedApp} from "../../utils/appConfig";
import zymedLogo from "../../assets/images/zymedelite.svg";
import {
    markPediaArticleOpen,
    pediaContentAttrs,
    startDwellTimer,
    trackZymedEvent,
    useZymedOpenedEvent,
    ZYMED_EVENTS,
} from "../../utils/zymedAnalytics";

const getActionText = (contentType: string) => {
  if (!contentType) return "Read";
  
  const normalizedType = contentType.toLowerCase().trim();
  switch (normalizedType) {
    // New format-based content types
    case "videos":
    case "video":
      return "Watch";
    case "documents":
    case "document":
      return "Read";
    case "podcasts":
    case "podcast":
      return "Listen";
    case "slides":
    case "slide":
      return "Read";
    
    // Legacy content type cases for backward compatibility
    case "clinical education":
      return "Read";
    case "guidelines & protocols":
      return "Read";
    case "medical news":
      return "Read";
    case "patient case studies":
      return "Read";
    case "patient information material":
      return "Read";
    case "procedures & surgeries":
      return "Watch";
    case "recent clinical advances":
      return "Read";
    case "webinars & panel discussions":
      return "Watch";
    case "editorials & research case studies":
      return "Read";
    case "infographics":
    case "medical slide library":
      return "Read";
    case "expert opinions":
      return "Watch";
    default:
      return "Read";
  }
};

const formatDuration = (duration: string) => {
  if (!duration || typeof duration !== 'string') return "";
  const [hours, minutes] = duration.split(":");
  const h = parseInt(hours) || 0;
  const m = parseInt(minutes) || 0;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return "";
};

const getDurationText = (contentDetails: any) => {
  if (!contentDetails) return "";
  const fileExtension = contentDetails?.uploadContentUrl?.split(".").pop()?.toLowerCase();
  const isDocumentType = fileExtension === "pdf" || fileExtension === "ppt" || fileExtension === "pptx";
  const isSlideContent = contentDetails?.contentType?.toLowerCase().includes("slide");
  const timeToShow = (isDocumentType || isSlideContent) ? contentDetails?.readTime : contentDetails?.duration;
  if (!timeToShow) return "";
  if (typeof timeToShow === 'string' && (timeToShow.includes('h') || timeToShow.includes('m'))) {
    return timeToShow;
  }
  if (typeof timeToShow === 'string' && timeToShow.includes(':')) {
    return formatDuration(timeToShow);
  }
  return formatDuration(timeToShow);
};

// If nothing is playing this long after the tap, stop spinning (tap-to-play or Retry)
const PLAY_START_TIMEOUT_MS = 15000;

const isPodcast = (contentDetails: any) =>
  (contentDetails?.contentType || "").toLowerCase().includes("podcast");

// Player failures were invisible (console only), so intermittent "not loading" reports
// could not be traced. `stage` says how far playback got.
const reportPlaybackIssue = (stage: string, contentDetails: any, extra: Record<string, unknown> = {}) => {
  Sentry.captureMessage(`Media playback failed: ${stage}`, {
    level: "warning",
    tags: {playback_stage: stage, content_type: contentDetails?.contentType},
    extra: {mediaId: contentDetails?.mediaId, contentId: contentDetails?.id, ...extra},
  });
};

const createAudioElement = () => {
  const audio = document.createElement("audio");
  audio.preload = "auto";
  return audio;
};

const createVideoElement = (contentDetails: any) => {
  const video = document.createElement("video");
  video.controls = true;
  video.playsInline = true;
  video.preload = "metadata";
  video.poster = contentDetails?.coverPhotoUrl || detailThumb;
  video.className = "w-full h-full bg-black object-contain";
  return video;
};

const isAudioContent = (contentDetails: any) =>
  contentDetails?.contentFormatId === 1002 ||
  contentDetails?.uploadContentUrl?.split(".").pop()?.toLowerCase() === "mp3" ||
  isPodcast(contentDetails);

// Opens the TCP+TLS connection to the MK.io origin (East US, ~0.6 s from India)
// before the player asks for the manifest. Added once; harmless if it goes unused.
const preconnectStreamingOrigin = () => {
  if (document.querySelector(`link[rel="preconnect"][href="${MEDIAKIND_STREAMING_ORIGIN}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = MEDIAKIND_STREAMING_ORIGIN;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
};

const isMediaContent = (contentDetails: any) => {
  if (!contentDetails) return false;
  if (contentDetails.contentFormatId === 1000 || contentDetails.contentFormatId === 1002) {
    return true;
  }
  const contentType = contentDetails.contentType?.toLowerCase() || "";
  const contentFormat = contentDetails.contentFormat?.toLowerCase() || "";
  if (contentType.includes("video") || contentFormat.includes("video") ||
      contentType.includes("podcast") || contentFormat.includes("podcast")) {
    return true;
  }
  const fileExtension = contentDetails.uploadContentUrl?.split(".").pop()?.toLowerCase() || "";
  const mediaExtensions = ["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv", "m4v", "mp3", "wav", "aac", "ogg"];
  if (mediaExtensions.includes(fileExtension)) {
    return true;
  }
  
  const legacyMediaTypes = [
    "procedures & surgeries",
    "webinars & panel discussions", 
    "expert opinions",
    "podcasts"
  ];
  
  if (legacyMediaTypes.some(type => contentType.includes(type))) {
    return true;
  }
  
  return false;
};

/** Maps the referring path stored in router state to the taxonomy's `source` values. */
const articleOpenSource = (from?: string) => {
  if (!from) return "feed_card";
  if (from === "/" || from === "/home") return "home_card";
  if (from.includes("/search")) return "search_result";
  return "feed_card";
};

export default function TatvaPediaHomeScreen() {
    const navigate = useNavigate();
    const {state} = useLocation();
    const {id, date, previousTab, source, currentTab} = state || {};
    // A Home card tap lands here without passing through /tatvapedia, so the feature
    // "open" is attributed on this screen when (and only when) Home recorded a source.
    useZymedOpenedEvent(ZYMED_EVENTS.PEDIA_OPENED, { requireSource: true });
    const {tatvaPediaToken} = useStore();
    const getToken = () =>
        tatvaPediaToken || localStorage.getItem("tatvaPediaToken");
    const token = getToken();
    const [contentDetails, setContentDetails] = useState<any>(null);
    const [quizDetails, setQuizDetails] = useState<any>(null);
    const [streamData, setStreamData] = useState<any>(null);
    const [isLoadingStream, setIsLoadingStream] = useState<boolean>(false);
    const [streamError, setStreamError] = useState<string | null>(null);
    const [isBuffering, setIsBuffering] = useState<boolean>(false);
    const [isPlayerReady, setIsPlayerReady] = useState<boolean>(false);
    const [audioPlayer, setAudioPlayer] = useState<any>(null); // loaded player, audio content only
    const playerRef = useRef<HTMLDivElement | null>(null); // Reference to the player container
    const playerInstanceRef = useRef<any>(null); // Reference to player instance
    // Stream data + player are loaded when the page opens, not on tap, so the
    // MK.io round trips (paths, manifest, playlists) overlap with reading time.
    // The tap then only has to start playback.
    const [isPlayRequested, setIsPlayRequested] = useState<boolean>(false);
    const playRequestedRef = useRef<boolean>(false); // read inside player callbacks
    const sourceLoadedRef = useRef<boolean>(false);
    // shared by prefetch and tap; keyed on mediaId so other content never reuses it
    const streamRequestRef = useRef<{mediaId: any; promise: Promise<any>} | null>(null);
    const activeMediaIdRef = useRef<any>(null); // mediaId the stream/play state belongs to
    const playStartedRef = useRef<boolean>(false);
    const playTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // iOS/iPadOS WebKit blocked a play() that did not come straight from a tap
    const [needsTapToPlay, setNeedsTapToPlay] = useState<boolean>(false);
    // ZymPedia_ArticleClosed is reported from the unmount, which covers the in-app back
    // button, the hardware back gesture and a jump straight to another route alike.
    const dwellRef = useRef<ReturnType<typeof startDwellTimer> | null>(null);
    const openContentRef = useRef<any>(null);
    useEffect(
        () => () => {
            const content = openContentRef.current;
            const time_spent_min = dwellRef.current?.stopMinutes();
            if (!content) return;
            trackZymedEvent(ZYMED_EVENTS.PEDIA_ARTICLE_CLOSED, {
                ...pediaContentAttrs(content),
                time_spent_min,
            });
        },
        []
    );

    const fetchContentDetail = async () => {
        const res: any = await getContentDetail(id, token);
        if (res !== undefined && res.hasOwnProperty("id")) {
            setContentDetails(res);
            openContentRef.current = res;
            markPediaArticleOpen();
            dwellRef.current = startDwellTimer();
            trackZymedEvent(ZYMED_EVENTS.PEDIA_ARTICLE_OPEN, {
                source: articleOpenSource(source),
                ...pediaContentAttrs(res),
            });

            fetchQuizDetails();

        } else if (res === undefined) {
            if (
        source &&
        (source.includes("/content-list") || source.includes("/search") || source.includes("/library"))
      ) {
                // Go back to the content list screen or library
                navigate(source);
      } else if (source === "/" || source === "/home") {
        // If source is home page, go back to home
        navigate("/");
            } else if (previousTab) {
                navigate(`/tatvapedia?activeTab=${previousTab}`);
            } else {
                // Fall back to default back navigation
                navigate(-1);
            }
        }
    };
    const fetchQuizDetails = async () => {
        const res: any = await getQuizStats(id, token);
        if (res !== undefined && res?.body?.length > 0) {
            setQuizDetails(res.body[0]);
            console.log('Quiz details {}', res.body[0]);
        }

    };
    useEffect(() => {
        if (id && token) {
            fetchContentDetail();
        }
    }, [id, token]);

    // one /media/stream call per media item, whether started by the prefetch or the tap
    const requestStream = (showError: boolean) => {
        const mediaId = contentDetails?.mediaId;
        if (!streamRequestRef.current || streamRequestRef.current.mediaId !== mediaId) {
            const promise = getMediaData(mediaId, token, showError).then((res: any) => {
                if ((res === undefined || res === null) && streamRequestRef.current?.promise === promise) {
                    streamRequestRef.current = null; // let the tap / retry try again
                }
                return res;
            });
            streamRequestRef.current = {mediaId, promise};
        }
        return streamRequestRef.current.promise;
    };

    const clearPlayTimeout = () => {
        if (playTimeoutRef.current) {
            clearTimeout(playTimeoutRef.current);
            playTimeoutRef.current = null;
        }
    };

    useEffect(() => clearPlayTimeout, []);

    // Prefetch as soon as the media page is known. Keyed on mediaId only:
    // contentDetails is replaced on like/bookmark and must not refetch.
    useEffect(() => {
        const mediaId = contentDetails?.mediaId;
        // Other content on the same mounted screen: drop the previous item's stream and play state
        if (activeMediaIdRef.current !== mediaId) {
            activeMediaIdRef.current = mediaId;
            streamRequestRef.current = null;
            sourceLoadedRef.current = false;
            playRequestedRef.current = false;
            playStartedRef.current = false;
            clearPlayTimeout();
            setIsPlayRequested(false);
            setNeedsTapToPlay(false);
            setStreamError(null);
            setStreamData(null);
        }
        if (!token || !mediaId || !isMediaContent(contentDetails)) {
            return;
        }
        preconnectStreamingOrigin();
        requestStream(false).then((res: any) => {
            // ignore a response that lands after the user moved to other content
            if (res && activeMediaIdRef.current === mediaId) {
                setStreamData((current: any) => current || res);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentDetails?.mediaId, token]);

    const handleBackClick = () => {
        if (
      source &&
      (source.includes("/content-list") || source.includes("/search") || source.includes("/library"))
    ) {
            // Go back to the content list screen or library
            if (source.includes("/library") && currentTab) {
                navigate(`/tatvapedia/library?activeTab=${encodeURIComponent(currentTab)}`);
            } else {
                navigate(source);
            }
        }
    // If source is home page, go back to home
    else if (source === "/" || source === "/home") {
      navigate("/");
    }
        // If we have a previous tab, navigate back to TatvaPediaHomeScreen with that tab
        else if (previousTab) {
            navigate(`/tatvapedia?activeTab=${encodeURIComponent(previousTab)}`);
        } else {
            // Fall back to default back navigation
            navigate(-1);
        }
    };

    const onQuizClick = async (toQuiz: boolean) => {
        navigate(toQuiz ? `/tatvapedia/quiz?contentInfoId=${id}` : `/tatvapedia/quiz-result?contentInfoId=${id}&quizId=${quizDetails?.quizId}&userInfoId=${quizDetails?.userInfoId}&backTo=content`);
    };

    const onDownloadClick = async () => {
        const res: any = await getContentUrl(
            contentDetails?.id,
            contentDetails?.contentTypeId,
            contentDetails?.uploadContentUrl,
            token
        );

        if (res !== undefined && res.hasOwnProperty("signedUrl")) {
            if (isZymedApp()) {
                sendMessageToParent("download", {url: res?.signedUrl});
            } else {
                try {
                    const response = await axios({
                        url: res?.signedUrl,
                        method: "GET",
                        responseType: "blob",
                    });

                    const blob = new Blob([response.data], {
                        type: response.headers["content-type"],
                    });
                    // saveAs(blob, `${Date.now()}.pdf`);
                    saveAs(blob, contentDetails?.uploadContentUrl);
                } catch (error) {
                    console.error("Error downloading file:", error);
                }
            }
        }
    };

    const onReadClick = async () => {
        trackZymedEvent(ZYMED_EVENTS.PEDIA_READ_ARTICLE_CLICKED, {
            content_id: contentDetails?.id,
            content_name: contentDetails?.contentTitle,
        });
        const res: any = await getContentUrl(
            contentDetails?.id,
            contentDetails?.contentTypeId,
            contentDetails?.uploadContentUrl,
            token
        );
        if (res !== undefined && res.hasOwnProperty("signedUrl")) {
            navigate("/tatvaPedia-document", {
                state: {contentData: {...contentDetails}, url: res?.signedUrl},
            });
        }
    };

    useEffect(() => {
        // Initialize MKPlayer when the URL is set
        if (streamData && playerRef.current) {
            if (!MKPLAYER_KEY) {
                console.error("MKPlayer key is not configured");
                setStreamError("Player configuration error");
                setIsLoadingStream(false);
                return;
            }

            // Capture contentDetails to avoid stale closure and prevent unnecessary re-renders
            const currentContentDetails = contentDetails;

            // Validate stream data before constructing HLS URL
            const mediaKindStreamingEndpoint = streamData?.mediaKindStreamingEndpoint;
            const hlsPath = streamData?.streamingPaths?.find(
                (e: any) => e.streamingProtocol === "Hls"
            )?.paths?.[0];

            if (!mediaKindStreamingEndpoint || !hlsPath) {
                console.error("Missing required streaming data:", {
                    hasEndpoint: !!mediaKindStreamingEndpoint,
                    hasHlsPath: !!hlsPath,
                    streamData
                });
                setStreamError("Streaming data is incomplete. Please try again.");
                setIsLoadingStream(false);
                return;
            }

            const hlsUrl = `${mediaKindStreamingEndpoint}${hlsPath}`;

            // Validate URL format
            try {
                new URL(hlsUrl);
            } catch (error) {
                console.error("Invalid HLS URL:", hlsUrl);
                setStreamError("Invalid streaming URL. Please try again.");
                setIsLoadingStream(false);
                return;
            }

            sourceLoadedRef.current = false;
            playStartedRef.current = false;
            const isAudio = isAudioContent(currentContentDetails);

            if (supportsNativeHls()) {
                const media = isAudio ? createAudioElement() : createVideoElement(currentContentDetails);
                if (!isAudio) playerRef.current.appendChild(media);
                let startReported = false;
                const nativePlayer = createNativeMediaPlayer(media, hlsUrl, {
                    onPlaying: () => {
                        playStartedRef.current = true;
                        clearPlayTimeout();
                        setNeedsTapToPlay(false);
                        setIsBuffering(false);
                        if (startReported) return;
                        startReported = true;
                        trackZymedEvent(ZYMED_EVENTS.PEDIA_CONTENT_PLAYBACK_STARTED, {
                            content_id: currentContentDetails?.id,
                            content_type: currentContentDetails?.contentType,
                        });
                    },
                    // Native video controls show their own spinner
                    onWaiting: () => setIsBuffering(isAudio),
                    onEnded: (watched, duration) => {
                        trackZymedEvent(ZYMED_EVENTS.PEDIA_CONTENT_PLAYBACK_COMPLETED, {
                            content_id: currentContentDetails?.id,
                            content_type: currentContentDetails?.contentType,
                            watch_percent: duration ? Math.round((watched / duration) * 100) : undefined,
                            watched_duration_min: Math.round((watched / 60) * 100) / 100,
                        });
                    },
                    onError: (details) => {
                        reportPlaybackIssue("native_audio_error", currentContentDetails, details);
                        clearPlayTimeout();
                        setStreamError(`Failed to load ${isAudio ? "audio" : "video"}. Please try again.`);
                        setIsBuffering(false);
                    },
                });
                playerInstanceRef.current = nativePlayer;
                sourceLoadedRef.current = true;
                setStreamError(null);
                if (isAudio) setAudioPlayer(nativePlayer);
                setIsPlayerReady(true);
                setIsBuffering(false);
                setIsLoadingStream(false);
                if (playRequestedRef.current) {
                    startPlayback(nativePlayer);
                }
                return () => {
                    setAudioPlayer(null);
                    nativePlayer.destroy();
                    playerInstanceRef.current = null;
                };
            }

            setIsPlayerReady(false);
            setIsBuffering(true);
            setStreamError(null);

            const playerConfig = {
                key: MKPLAYER_KEY,
                // audio has its own controls over the cover image
                ui: !isAudio,
                playback: {
                    muted: false,
                    // iOS: play inside the page, not in the native fullscreen player
                    playsInline: true,
                    // started explicitly: on tap, or right after load if the tap came first
                    autoplay: false,
                },
                // Podcasts: also buffer the first audio segments before the tap (small).
                // Videos: only manifest/playlists, so an unplayed video costs no segment bandwidth.
                adaptation: {
                    [MKPlatformType.Desktop]: {preload: isPodcast(currentContentDetails)},
                    [MKPlatformType.Mobile]: {preload: isPodcast(currentContentDetails)},
                },
                // Subscribe to player events
                events: {
                    [MKPlayerEvent.Playing]: () => {
                        playStartedRef.current = true;
                        clearPlayTimeout();
                        setNeedsTapToPlay(false);
                    },
                    [MKPlayerEvent.Error]: (event: any) => {
                        console.error("Player error:", event);
                        reportPlaybackIssue("player_error", currentContentDetails, {
                            code: event?.code,
                            name: event?.name,
                            message: event?.message,
                        });
                        clearPlayTimeout();
                        setStreamError("Failed to load video. Please try again.");
                        setIsBuffering(false);
                        setIsLoadingStream(false);
                    },
                    [MKPlayerEvent.TimeChanged]: (event: any) => {
                        // console.log("Current player position: ", event.time);
                    },
                    [MKPlayerEvent.StallStarted]: () => {
                        setIsBuffering(true);
                    },
                    [MKPlayerEvent.StallEnded]: () => {
                        setIsBuffering(false);
                    },
                },
            };

            // Playback taxonomy. `Play` fires on resume as well, so the "started" event
            // is reported once per loaded source; `playbackfinished` is the natural
            // completion signal and carries the percentage actually watched.
            const playbackContentType = currentContentDetails?.contentType;
            let playbackStartReported = false;

            const player: any = new MKPlayer(playerRef.current, playerConfig);
            playerInstanceRef.current = player;
            let isPlayerDestroyed = false;

            player.on(MKPlayerEvent.SourceLoaded, (event: any) => {
                console.log("Source loaded successfully!");
            });

            player.on(MKPlayerEvent.Play, () => {
                if (playbackStartReported) return;
                playbackStartReported = true;
                trackZymedEvent(ZYMED_EVENTS.PEDIA_CONTENT_PLAYBACK_STARTED, {
                    content_id: currentContentDetails?.id,
                    content_type: playbackContentType,
                });
            });

            player.on(MKPlayerEvent.PlaybackFinished, () => {
                const duration = Number(player?.getDuration?.()) || 0;
                const watched = Number(player?.getCurrentTime?.()) || duration;
                trackZymedEvent(ZYMED_EVENTS.PEDIA_CONTENT_PLAYBACK_COMPLETED, {
                    content_id: currentContentDetails?.id,
                    content_type: playbackContentType,
                    watch_percent: duration ? Math.round((watched / duration) * 100) : undefined,
                    watched_duration_min: Math.round((watched / 60) * 100) / 100,
                });
            });

            player.on(MKPlayerEvent.PlaybackSpeedChanged, (event: any) => {
                const speed = event?.to ?? event?.speed ?? player?.getPlaybackSpeed?.();
                trackZymedEvent(ZYMED_EVENTS.PEDIA_PLAYBACK_SPEED_CHANGED, {
                    content_id: currentContentDetails?.id,
                    speed_value: speed === undefined ? undefined : `${speed}x`,
                });
            });

            player.on(MKPlayerEvent.Ready, (event: any) => {
                console.log("Player is ready for playback!");
                setIsPlayerReady(true);
                setIsBuffering(false);
                setIsLoadingStream(false);
            });

            const sourceConfig = {
                title: currentContentDetails?.contentTitle || "Title for your source",
                // description: "Brief description of your source",
                poster: currentContentDetails?.coverPhotoUrl || detailThumb,
                hls: hlsUrl,
            };

            player
                .load(sourceConfig)
                .then(() => {
                    // Ignore if player was destroyed during load
                    if (isPlayerDestroyed) return;
                    sourceLoadedRef.current = true;
                    // source is ready: clear the overlays even if Ready never fires (iOS app WebView)
                    setIsLoadingStream(false);
                    setIsBuffering(false);
                    if (playRequestedRef.current) {
                        startPlayback(player);
                    }

                    console.log("Source loaded successfully!");
                    if (isAudio) {
                        setAudioPlayer(player);
                    }
                    const video: any = document.getElementById(
                        "bitmovinplayer-video-video-container"
                    );
                    if (video) {
                        // Without the size the element overflows the player and covers the page
                        video.style = "width: 100%;height: 100%";
                    }
                })
                .catch((error: any) => {
                    // Ignore errors from destroyed players
                    if (isPlayerDestroyed || error?.message?.includes("being destroyed")) {
                        return;
                    }
                    console.error("An error occurred while loading the source:", error);
                    reportPlaybackIssue("source_load_failed", currentContentDetails, {message: error?.message});
                    clearPlayTimeout();
                    setStreamError("Failed to load video. Please try again.");
                    setIsBuffering(false);
                    setIsLoadingStream(false);
                });

            return () => {
                // Mark player as destroyed before cleanup
                isPlayerDestroyed = true;
                setAudioPlayer(null);
                // Cleanup player when the component is unmounted
                if (playerInstanceRef.current) {
                    try {
                        playerInstanceRef.current.destroy();
                    } catch (error) {
                        console.error("Error destroying player:", error);
                    }
                    playerInstanceRef.current = null;
                }
            };
        }
    }, [streamData]);

    const startPlayback = (player: any) => {
        const onBlocked = (error: any) => {
            console.warn("Playback did not start:", error);
            reportPlaybackIssue("play_rejected", contentDetails, {name: error?.name, message: error?.message});
            // iOS/iPadOS WebKit rejects a play() that is not inside a tap (NotAllowedError).
            // Show a play button so the next play() comes straight from a tap.
            if (!playStartedRef.current) {
                setNeedsTapToPlay(true);
            }
        };
        try {
            Promise.resolve(player.play()).catch(onBlocked);
        } catch (error) {
            onBlocked(error);
        }
    };

    // Never spin forever: if nothing is playing PLAY_START_TIMEOUT_MS after the tap, offer
    // tap-to-play (source loaded, play blocked) or the Retry screen (source never loaded).
    const armPlayTimeout = () => {
        clearPlayTimeout();
        playTimeoutRef.current = setTimeout(() => {
            playTimeoutRef.current = null;
            if (playStartedRef.current) return;
            const stage = sourceLoadedRef.current ? "play_blocked" : streamRequestRef.current ? "source_load_timeout" : "stream_timeout";
            reportPlaybackIssue(stage, contentDetails, {timeoutMs: PLAY_START_TIMEOUT_MS});
            if (sourceLoadedRef.current) {
                setNeedsTapToPlay(true);
            } else {
                setStreamError("Taking too long to load. Please try again.");
                setIsBuffering(false);
                setIsLoadingStream(false);
            }
        }, PLAY_START_TIMEOUT_MS);
    };

    const fetchStreamMediaAPI = async () => {
        if (!contentDetails?.mediaId) {
            console.error("No mediaId found in content details");
            setStreamError("Media ID not found");
            return;
        }

        setIsLoadingStream(true);
        setStreamError(null);

        try {
            const mediaId = contentDetails?.mediaId;
            const res: any = await requestStream(true);
            if (activeMediaIdRef.current !== mediaId) return; // user moved to other content
            if (res !== undefined && res !== null) {
                setStreamData(res);
            } else {
                setStreamError("Failed to load streaming data. Please try again.");
                setIsLoadingStream(false);
            }
        } catch (error: any) {
            console.error("Error fetching stream data:", error);
            setStreamError("Network error. Please check your connection and try again.");
            setIsLoadingStream(false);
        }
    };

    const handlePlayClick = () => {
        playRequestedRef.current = true;
        setIsPlayRequested(true);
        armPlayTimeout();
        const player = playerInstanceRef.current;
        if (player && sourceLoadedRef.current) {
            // preloaded: start right away, inside the tap so browsers allow audio
            startPlayback(player);
        } else if (!streamData) {
            // prefetch not finished or failed; the player effect plays once loaded
            fetchStreamMediaAPI();
        }
        // else: player is still loading; load().then starts playback
    };

    // play() from this tap is allowed even where the automatic one was blocked
    const handleTapToPlay = () => {
        setNeedsTapToPlay(false);
        armPlayTimeout();
        const player = playerInstanceRef.current;
        if (player) {
            startPlayback(player);
        }
    };

    const handleRetryStream = () => {
        // force a fresh stream call and a new player instance
        streamRequestRef.current = null;
        sourceLoadedRef.current = false;
        setNeedsTapToPlay(false);
        setStreamData(null);
        setStreamError(null);
        if (playRequestedRef.current) {
            armPlayTimeout();
        }
        fetchStreamMediaAPI();
    };

    return (
        <div>
            <div className="sticky top-0 bg-white z-10">
                <div
                    className="flex gap-1.5 items-center px-3 py-4 text-base font-semibold tracking-tight text-center bg-white border-b border-solid border-b-zinc-200 text-[#1F2933]">
                    <img
                        loading="lazy"
                        src={backIcon}
                        className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
                        onClick={handleBackClick}
                    />
                    {contentDetails ? contentDetails?.internalName : ""}
                </div>
            </div>

      {contentDetails ? (
        <div
          className="overflow-y-auto"
          style={{ height: "calc(100vh - 57px)" }}
        >
          <div className="relative">
            {streamError ? (
              <div 
                className="h-[240px] bg-black relative flex items-center justify-center"
                style={{ overflow: "hidden" }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center gap-4 p-4 z-10">
                  <div className="text-white text-center">
                    <div className="text-lg font-semibold mb-2">Unable to load {isAudioContent(contentDetails) ? "podcast" : "video"}</div>
                    <div className="text-sm text-gray-300 mb-4">{streamError}</div>
                    <Button
                      type="primary"
                      onClick={handleRetryStream}
                      className="bg-primary"
                    >
                      Retry
                    </Button>
                  </div>
                </div>
                <OptimizedImage
                  src={contentDetails?.coverPhotoUrl || detailThumb}
                  alt={contentDetails?.contentTitle}
                  className="max-h-full max-w-full object-contain opacity-30"
                  fallbackSrc={detailThumb}
                  useIntersectionObserver={false}
                />
              </div>
            ) : (
              <div className={`relative ${isAudioContent(contentDetails) ? "h-[240px]" : "aspect-video max-h-[60vh] w-full"}`}>
                {streamData && (
                  <div className="absolute inset-0 overflow-hidden">
                    <div
                      ref={playerRef}
                      id="video-container"
                      className="h-full"
                      style={{ position: "relative" }}
                    />
                    {/* A podcast has no picture: keep the cover up while it plays */}
                    {isAudioContent(contentDetails) && (
                      <div className="absolute inset-0 z-[5] bg-black flex items-center justify-center overflow-hidden">
                        <OptimizedImage
                          src={contentDetails?.coverPhotoUrl || detailThumb}
                          alt={contentDetails?.contentTitle}
                          className="max-h-full max-w-full object-contain"
                          fallbackSrc={detailThumb}
                          useIntersectionObserver={false}
                        />
                      </div>
                    )}
                    {audioPlayer && isPlayRequested && <PodcastControls player={audioPlayer} />}
                    {/* Buffering Indicator */}
                    {isBuffering && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 transition-opacity duration-300">
                        <div className="flex flex-col items-center gap-3">
                          <Spin size="small" />
                          <span className="text-white text-sm">Buffering...</span>
                        </div>
                      </div>
                    )}
                    {/* Loading Indicator while player initializes */}
                    {isLoadingStream && !isPlayerReady && (
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-10 transition-opacity duration-300">
                        <div className="flex flex-col items-center gap-3">
                          <Spin size="small" />
                          <span className="text-white text-sm">Loading player...</span>
                        </div>
                      </div>
                    )}
                    {/* iOS blocked automatic play: this tap starts it */}
                    {needsTapToPlay && (
                      <div className="absolute inset-0 z-30 bg-black bg-opacity-60 flex flex-col items-center justify-center gap-2">
                        <button
                          className="w-[3rem] h-[3rem] bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
                          onClick={handleTapToPlay}
                          aria-label="Play"
                        >
                          <IoPlay className={"black"} size={22} />
                        </button>
                        <span className="text-white text-sm">Tap to play</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Poster + play button stay on top until the tap; the player loads behind them */}
                {(!isPlayRequested || !streamData) && (
                  <div
                    className="absolute inset-0 z-20 bg-black flex items-center justify-center transition-opacity duration-300"
                    style={{ overflow: "hidden" }}
                  >
                    <OptimizedImage
                      src={contentDetails?.coverPhotoUrl || detailThumb}
                      alt={contentDetails?.contentTitle}
                      className="max-h-full max-w-full object-contain"
                      fallbackSrc={detailThumb}
                      useIntersectionObserver={false}
                    />
                    {/* Loading overlay when the tap came before the stream data */}
                    {isPlayRequested && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10">
                        <div className="flex flex-col items-center gap-3">
                          <Spin size="small" />
                          <span className="text-white text-sm">Loading stream...</span>
                        </div>
                      </div>
                    )}
                    {isMediaContent(contentDetails) && !isPlayRequested && (
                      <button
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[3rem] h-[3rem] bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200 z-20"
                        onClick={handlePlayClick}
                      >
                        <div className="rounded-full flex items-center justify-center">
                          <IoPlay className={"black"} size={22} />
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* <video style={{ maxHeight: 220 }} className="w-full shadow-lg" autoPlay loop controls>
		  <source
			src="https://tecdn.b-cdn.net/img/video/Sail-Away.mp4"
			type="video/mp4" />
		</video>
		<div className="relative">
		  <img loading="lazy" src={tatvaImg} style={{ maxHeight: 220 }} />
		  <audio className="absolute left-1/2 top-1/2"
			style={{ transform: 'translate(-50%, -50%)' }}
			src='https://file-examples.com/storage/fe11f9541a67d9f2f9b2038/2017/11/file_example_MP3_700KB.mp3'
			controls>
		  </audio>
		</div> */}
                    <div className="p-5 text-left break-words overflow-hidden">
                        <div className="font-semibold text-[20px] text-[#454551]">
                            {contentDetails?.contentTitle}
                        </div>

            <div className="text-[#A2A2A8] text-[12px] py-2">
              {moment(date).format("DD MMM YYYY")}
              {/* {getDurationText(contentDetails) && (
                <>
                  {" | "}
                  {getDurationText(contentDetails)} {getActionText(contentDetails?.contentType)}
                </>
              )} */}
            </div>

                        <LikeShareBookmark
                            type={1}
                            contentDetails={contentDetails}
                            setContentDetails={setContentDetails}
                        />

                        <div className="text-[#454551] text-[14px] font-light py-2">
                            {contentDetails?.contentDescription}
                        </div>
                        {contentDetails?.references && (
                            <div className="text-[#454551] text-[14px] font-medium py-3">
                                Reference: {contentDetails?.references}
                            </div>
                        )}
                        {(contentDetails?.kolSpeaker?.length > 0 ||
                            contentDetails?.kolHost?.length > 0) && (
                            <div className="my-3 px-3 py-4 border-[#E2E2EA] bg-[#FAFAFB] rounded-[10px] border">
                                {contentDetails?.kolSpeaker?.length > 0 && (
                                    <>
                                        <div className="text-[14px] text-[#5A6774]">Speaker:</div>
                                        <div className="flex items-center py-3 border-b">
                                            <div>
                                                <FaUser
                                                    className="rounded-3xl w-[45px] h-[45px] top-[8px] left-[8px] text-[#5A6774] bg-[#F1F1F5] p-2"/>
                                            </div>
                                            <div className="text-sm text-[#454551] font-medium ml-4">
                                                {contentDetails?.kolSpeaker?.[0]?.name}
                                                <div className="text-[12px] text-[#A2A2A8]">
                                                    {`${contentDetails?.kolSpeaker?.[0]?.designation} | ${contentDetails?.kolSpeaker?.[0]?.qualification}`}
                                                    <br/>
                                                    {contentDetails?.kolSpeaker?.[0]?.organization}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div
                                    className={`flex items-center ${
                                        contentDetails?.kolSpeaker?.length > 0 && "pt-2"
                                    }`}
                                >
                                    <div className="text-[14px] text-[#5A6774] mr-2">
                                        Hosted by:
                                    </div>
                                    <img
                                        loading="lazy"
                                        src={`${
                                            isZymedApp()
                        ? zymedLogo
                        : contentDetails?.kolSpeaker?.[0]?.imageUrl
                                                ? contentDetails?.kolSpeaker?.[0]?.imageUrl
                                                : TatvaPracticecolorLogo
                                        }`}
                                        style={{height: 29}}
                                        alt={contentDetails?.kolSpeaker?.[0]?.imageName}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between bg-white sticky bottom-0">
                            {quizDetails?.quizId && quizDetails?.userInfoId === null && <Button
                                className="my-2 font-manrope font-bold p-6 w-full me-3 rounded-[10px] text-lg text-primary border-primary"
                                onClick={()=>onQuizClick(true)}
                            >
                                Take Quiz
                            </Button>}
                            {quizDetails?.quizId && quizDetails?.userInfoId !== null && <Button
                                className="my-2 font-manrope font-bold p-6 w-full me-3 rounded-[10px] text-lg text-primary border-primary"
                                onClick={()=>onQuizClick(false)}
                            >
                                View Result
                            </Button>}
                            {/*{ contentDetails?.isDownload &&
                contentDetails?.uploadContentUrl
                  .split(".")
                  .pop()
                  .toLowerCase() === "pdf" && (
                  <Button
                    className="my-2 font-manrope font-bold p-6 w-full me-3 rounded-[10px] text-lg text-primary border-primary"
                    onClick={onDownloadClick}
                  >
                    Download
                  </Button>
                )}*/}
                            {contentDetails?.uploadContentUrl
                                ?.split(".")
                                ?.pop()
                                ?.toLowerCase() === "pdf" && (
                                <Button
                                    className="my-2 font-manrope font-bold p-6 w-full rounded-[10px] text-lg bg-primary text-white"
                                    onClick={onReadClick}
                                >
                                    Read
                                </Button>
                            )}
                            {(contentDetails?.uploadContentUrl
                                ?.split(".")
                                ?.pop()
                                ?.toLowerCase() === "ppt" ||
                                contentDetails?.uploadContentUrl
                                    ?.split(".")
                                    ?.pop()
                                    ?.toLowerCase() === "pptx") && (
                                <Button
                                    className="my-2 font-manrope font-bold p-6 w-full rounded-[10px] text-lg bg-primary text-white"
                                    onClick={onReadClick}
                                >
                                    View Slides
                                </Button>
                            )}
                        </div>

                        {/* <div className="flex items-center justify-between py-3">
							<div className="font-semibold text-[20px] text-[#1F2933]">
								Write your review
							</div>
							<div className="text-sm underline text-primary font-medium" onClick={() => navigate('/comment-thread', { state: { url: "" } })}>
								All review
							</div>
						</div> */}

                        <CommentSection contentDetails={contentDetails}/>
                    </div>
                </div>
            ) : (
                <div
                    className="content-center flex items-center justify-center"
                    style={{height: "calc(100vh - 57px)"}}
                >
                    <Spin/>
                </div>
            )}
        </div>
    );
}
